// Pure helpers to separate cart lines by type, and to validate/shape a public
// Combo DTO before it enters the cart. Used before ANY call to the cart
// backend (getCartItems/setCart/deleteCartItem), which only understands
// Products — a Combo line must never be sent there.

export const isComboCartItem = (item) => item?.itemType === 'combo';

export const splitCartItems = (items = []) => {
    const productItems = [];
    const comboItems = [];
    (Array.isArray(items) ? items : []).forEach((item) => {
        if (isComboCartItem(item)) comboItems.push(item);
        else productItems.push(item);
    });
    return { productItems, comboItems };
};

export const getProductItemsFrom = (items = []) => splitCartItems(items).productItems;

export const getComboItemsFrom = (items = []) => splitCartItems(items).comboItems;

export const hasComboItemsIn = (items = []) => splitCartItems(items).comboItems.length > 0;

export const isComboInList = (items = [], comboId) =>
    splitCartItems(items).comboItems.some((item) => item._id === comboId);

// The server never reveals the exact stock number. Immediate combos require
// inventory; reservable combos remain purchasable while their credentials
// are prepared after payment.
export const isValidFreshCombo = (combo) => {
    if (!combo || typeof combo !== 'object') return false;
    if (!['AVAILABLE', 'RESERVABLE'].includes(combo.availabilityState) || combo.available !== true) return false;
    if (!Array.isArray(combo.items) || combo.items.length < 2) return false;
    return combo.items.every((item) => {
        const quantity = Number(item?.requiredQuantity);
        return !!item?.serviceId && Number.isInteger(quantity) && quantity > 0;
    });
};

// Explicit whitelist transformation from the public Combo DTO to a cart line.
// Never spreads the DTO: only these fields ever enter the cart, and the
// nested Services travel exactly as the server sent them (serviceId is kept
// so Gold Client can later hydrate the real Service into the existing cart).
export const toComboCartLine = (combo) => ({
    _id: combo._id,
    itemType: 'combo',
    cartKey: `combo:${combo._id}`,
    name: combo.name,
    description: combo.description,
    image: combo.imageUrl,
    specialPrice: combo.specialPrice,
    individualTotal: combo.individualTotal,
    savings: combo.savings,
    discountPercentage: combo.discountPercentage,
    availabilityState: combo.availabilityState,
    deliveryMode: combo.deliveryMode,
    estimatedMinutes: combo.estimatedMinutes,
    quantityToBuy: 1,
    items: (Array.isArray(combo.items) ? combo.items : []).map((item) => ({
        serviceId: item.serviceId,
        serviceName: item.serviceName,
        requiredQuantity: item.requiredQuantity,
        position: item.position
    }))
});
